// The calendar-feed function handles, and the one piece of deployment knowledge the
// settings surface needs (bead hf-7o6).
//
// Same shape as ./plan.ts: named handles re-exporting the generated api, plus result
// types inferred with `FunctionReturnType` so the browser cannot drift from the
// backend. The feature module imports only the TYPES from here, never the handles —
// it is a pure component tree over view models, exactly like features/plan.

import type { FunctionReturnType } from "convex/server";

import { api } from "../../../../convex/_generated/api";

/** This member's own feed. Never returns a token; see convex/domain/calendarFeeds.ts. */
export const myFeedQuery = api.domain.calendarFeeds.myFeed;

/**
 * Mint this member's link — which is ALSO how rotation happens.
 *
 * There is deliberately no separate "rotate" function: calling this again overwrites
 * the stored hash, so the previous link stops working the instant it returns.
 */
export const mintFeedTokenMutation = api.domain.calendarFeeds.mintFeedToken;
export const revokeFeedTokenMutation = api.domain.calendarFeeds.revokeFeedToken;
export const setFeedTitleModeMutation = api.domain.calendarFeeds.setFeedTitleMode;

export type MyFeedResult = FunctionReturnType<typeof myFeedQuery>;
export type MintFeedTokenResult = FunctionReturnType<typeof mintFeedTokenMutation>;
export type RevokeFeedTokenResult = FunctionReturnType<typeof revokeFeedTokenMutation>;
export type SetFeedTitleModeResult = FunctionReturnType<typeof setFeedTitleModeMutation>;

/** Where the feed route lives, matching ICS_FEED_PATH_PREFIX in convex/http/icsFeed.ts. */
export const FEED_PATH_PREFIX = "/feeds/ics/";

/**
 * Where HTTP actions are served.
 *
 * PREFER THE CONFIGURED VALUE. `convex/CONTRACT.md` documents `VITE_CONVEX_SITE_URL`
 * as the public HTTP-actions endpoint, and a value the deployment told us beats one we
 * worked out from the other URL. `siteUrl` is that value when it is configured.
 *
 * Only when it is absent do we derive: Convex answers function calls on
 * `<name>.convex.cloud` and HTTP actions on `<name>.convex.site` — a different host,
 * not a different path — while the local anonymous backend splits by port instead,
 * functions on 3210 and HTTP actions on 3211. Both are Convex's conventions rather
 * than ours, which is why this is one small pure function with tests rather than a
 * string concatenation at a call site.
 *
 * A member handed a link built against the wrong origin has a link their calendar app
 * cannot fetch. That fails safely — nothing leaks, it simply does not work — but it
 * wastes the one time we show it, so it is worth being exact about.
 *
 * The last fallback returns the origin unchanged. It is reached only by a deployment
 * shape this project does not have; returning the input beats inventing a host.
 */
export function feedOriginFrom(convexUrl: string, siteUrl?: string): string {
  if (siteUrl !== undefined && siteUrl.length > 0) {
    try {
      return new URL(siteUrl).origin;
    } catch {
      // A malformed configured value falls through to the derivation rather than
      // being handed to somebody as a link.
    }
  }

  let url: URL;
  try {
    url = new URL(convexUrl);
  } catch {
    return convexUrl;
  }

  if (url.hostname.endsWith(".convex.cloud")) {
    url.hostname = `${url.hostname.slice(0, -".convex.cloud".length)}.convex.site`;
    return url.origin;
  }

  if (url.port !== "") {
    url.port = String(Number(url.port) + 1);
    return url.origin;
  }

  return url.origin;
}
